import { Component, computed, inject, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { LocalizationService } from '../../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../../shared/design-system/info-hint.component';
import { FormScript, suggestScriptRef } from '../../../shared/dynamic-form/form-schema.models';

@Component({
  standalone: true,
  selector: 'ain-form-builder-scripts-panel',
  imports: [
    FormsModule,
    ButtonModule,
    CheckboxModule,
    InputTextModule,
    TextareaModule,
    MessageModule,
    SelectModule,
    AinFormFieldComponent,
    InfoHintComponent
  ],
  template: `
    <section class="scripts-panel">
      <ain-info-hint
        [titleKey]="'dynamic.builder.scripts.infoTitle'"
        [descriptionKey]="'dynamic.builder.scripts.infoDescription'"
        [bulletKeys]="[
          'dynamic.builder.scripts.infoBulletSandbox',
          'dynamic.builder.scripts.infoBulletApi'
        ]" />

      <p-message severity="warn" [text]="t('dynamic.builder.scripts.securityWarning')" />

      <div class="toolbar">
        <strong>{{ t('dynamic.builder.scripts.title') }}</strong>
        @if (canEdit()) {
          <button pButton type="button" text icon="pi pi-plus" [label]="t('dynamic.builder.scripts.add')" (click)="addScript()"></button>
        }
      </div>

      @for (script of scripts(); track script.ref; let idx = $index) {
        <article class="script-card">
          <div class="script-header">
            <strong>{{ script.ref }}</strong>
            @if (canEdit()) {
              <button pButton type="button" severity="danger" text icon="pi pi-trash" (click)="removeScript(idx)"></button>
            }
          </div>

          <div class="checkbox-row">
            <p-checkbox
              binary
              [ngModel]="script.enabled"
              [disabled]="!canEdit()"
              (ngModelChange)="updateScript(idx, { enabled: $event })" />
            <label>{{ t('dynamic.builder.scripts.enabled') }}</label>
          </div>

          <ain-form-field [label]="t('dynamic.builder.scripts.ref')">
            <input
              pInputText
              [ngModel]="script.ref"
              [disabled]="!canEdit()"
              (ngModelChange)="updateScript(idx, { ref: $event })" />
          </ain-form-field>

          <ain-form-field [label]="t('dynamic.builder.scripts.language')">
            <p-select
              [options]="languageOptions"
              optionLabel="label"
              optionValue="value"
              [ngModel]="script.language"
              [disabled]="!canEdit()"
              appendTo="body"
              (ngModelChange)="updateScript(idx, { language: $event })" />
          </ain-form-field>

          <ain-form-field [label]="t('dynamic.builder.scripts.code')">
            <textarea
              pTextarea
              class="code-editor"
              rows="10"
              [ngModel]="script.code"
              [disabled]="!canEdit()"
              (ngModelChange)="updateScript(idx, { code: $event })"></textarea>
          </ain-form-field>
        </article>
      } @empty {
        <p class="empty">{{ t('dynamic.builder.scripts.empty') }}</p>
      }
    </section>
  `,
  styles: [`
    .scripts-panel { display: grid; gap: 12px; }
    .toolbar { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .script-card {
      border: 1px solid var(--surface-border);
      border-radius: 8px;
      padding: 12px;
      display: grid;
      gap: 10px;
    }
    .script-header { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .checkbox-row { display: flex; align-items: center; gap: 8px; }
    .code-editor { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; }
    .empty { margin: 0; color: var(--text-color-secondary); }
  `]
})
export class FormBuilderScriptsPanelComponent {
  private readonly localization = inject(LocalizationService);

  readonly scripts = input<FormScript[]>([]);
  readonly canEdit = input(true);

  readonly scriptsChange = output<FormScript[]>();

  readonly scriptRefs = computed(() => (this.scripts() ?? []).map(item => item.ref));

  readonly languageOptions = [
    { label: 'JavaScript', value: 'javascript' },
    { label: 'CSS', value: 'css' }
  ];

  t(key: string): string {
    return this.localization.translate(key);
  }

  addScript(): void {
    const ref = suggestScriptRef(this.scriptRefs());
    this.scriptsChange.emit([
      ...(this.scripts() ?? []),
      { ref, language: 'javascript', code: '', enabled: true }
    ]);
  }

  removeScript(index: number): void {
    this.scriptsChange.emit((this.scripts() ?? []).filter((_, idx) => idx !== index));
  }

  updateScript(index: number, patch: Partial<FormScript>): void {
    const next = [...(this.scripts() ?? [])];
    const current = next[index];
    next[index] = {
      ...current,
      ...patch,
      language: patch.language ?? current.language ?? 'javascript'
    };
    this.scriptsChange.emit(next);
  }
}
